import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { AuthCard } from '../components/AuthCard';
import { AuthLayout } from '../components/AuthLayout';
import { PasswordField } from '../components/PasswordField';
import { AUTH_ROUTES } from '../routes';
import {
  forgotPasswordConfirmSchema,
  forgotPasswordRequestSchema,
  type ForgotPasswordConfirmFormValues,
  type ForgotPasswordRequestFormValues,
} from '../schemas';
import { authService } from '../services/authService';

type FlowStep = 'request' | 'confirm' | 'done';

export function ForgotPasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const [step, setStep] = useState<FlowStep>('request');
  const [username, setUsername] = useState('');

  if (step === 'request') {
    return (
      <AuthLayout>
        <AuthCard
          title="Recuperar contraseña"
          subtitle="Ingresá tu usuario para recibir un código por email"
        >
          <RequestStep
            onSuccess={(u) => {
              setUsername(u);
              setStep('confirm');
            }}
          />
        </AuthCard>
      </AuthLayout>
    );
  }

  if (step === 'confirm') {
    return (
      <AuthLayout>
        <AuthCard
          title="Verificar código"
          subtitle="Ingresá el código que recibiste por email y tu nueva contraseña"
        >
          <ConfirmStep username={username} onSuccess={() => setStep('done')} />
        </AuthCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthCard title="Contraseña actualizada">
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Tu contraseña fue actualizada. Ya podés iniciar sesión.
          </Typography>
          <Button variant="contained" onClick={() => navigate(AUTH_ROUTES.LOGIN)}>
            Ir al login
          </Button>
        </Stack>
      </AuthCard>
    </AuthLayout>
  );
}

function RequestStep({ onSuccess }: { onSuccess: (username: string) => void }): JSX.Element {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordRequestFormValues>({
    resolver: zodResolver(forgotPasswordRequestSchema),
    defaultValues: { username: '' },
  });

  async function handleFormSubmit(values: ForgotPasswordRequestFormValues): Promise<void> {
    setSubmitError(null);
    try {
      await authService.forgotPassword(values.username);
      onSuccess(values.username);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Error al solicitar el código');
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <Stack spacing={2}>
        {submitError && <Alert severity="error">{submitError}</Alert>}

        <TextField
          label="Usuario"
          autoFocus
          fullWidth
          autoComplete="username"
          {...register('username')}
          error={!!errors.username}
          helperText={errors.username?.message}
        />

        <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth>
          {isSubmitting ? 'Enviando...' : 'Enviar código'}
        </Button>

        <Link
          component={RouterLink}
          to={AUTH_ROUTES.LOGIN}
          variant="body2"
          sx={{ textAlign: 'center' }}
        >
          Volver al login
        </Link>
      </Stack>
    </form>
  );
}

interface ConfirmStepProps {
  username: string;
  onSuccess: () => void;
}

function ConfirmStep({ username, onSuccess }: ConfirmStepProps): JSX.Element {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordConfirmFormValues>({
    resolver: zodResolver(forgotPasswordConfirmSchema),
    defaultValues: { code: '', newPassword: '', confirmPassword: '' },
  });

  async function handleFormSubmit(values: ForgotPasswordConfirmFormValues): Promise<void> {
    setSubmitError(null);
    try {
      await authService.confirmForgotPassword(username, values.code, values.newPassword);
      onSuccess();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Error al actualizar la contraseña');
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <Stack spacing={2}>
        {submitError && <Alert severity="error">{submitError}</Alert>}

        <TextField
          label="Código de verificación"
          autoFocus
          fullWidth
          inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'one-time-code' }}
          {...register('code')}
          error={!!errors.code}
          helperText={errors.code?.message}
        />

        <PasswordField
          label="Nueva contraseña"
          fullWidth
          autoComplete="new-password"
          {...register('newPassword')}
          error={!!errors.newPassword}
          helperText={errors.newPassword?.message}
        />

        <PasswordField
          label="Confirmar nueva contraseña"
          fullWidth
          autoComplete="new-password"
          {...register('confirmPassword')}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
        />

        <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth>
          {isSubmitting ? 'Actualizando...' : 'Actualizar contraseña'}
        </Button>
      </Stack>
    </form>
  );
}
